import React, { useState, useEffect } from 'react';
import './Reviews.css';
import StarRating from './StarRating';
import { db, storage } from '../firebase';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

function ReviewPhotoSlider({ urls, name }) {
    const [current, setCurrent] = useState(0);

    useEffect(() => {
        if (urls.length <= 1) return;
        const interval = setInterval(() => {
            setCurrent(prev => (prev + 1) % urls.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [urls.length]);

    return (
        <div className='review-card__photos'>
            {urls.map((url, i) => (
                <img
                    key={i}
                    src={url}
                    alt={`Review by ${name}`}
                    className={`review-card__slide ${i === current ? 'review-card__slide--active' : 'review-card__slide--hidden'}`}
                />
            ))}
        </div>
    );
}

function Reviews() {
    const [reviews, setReviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({ name: '', rating: 0, text: '' });
    const [photos, setPhotos] = useState([]);
    const [photoPreviews, setPhotoPreviews] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState('idle');
    const [dragging, setDragging] = useState(false);

    useEffect(() => {
        const q = query(
            collection(db, 'reviews'),
            orderBy('createdAt', 'desc')
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const reviewData = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(review => review.hidden !== true)
                .slice(0, 12);
            setReviews(reviewData);
            setLoading(false);
        }, (error) => {
            console.error('Reviews query error:', error);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const MAX_PHOTOS = 5;
    const MAX_SIZE = 5 * 1024 * 1024;

    const compressImage = (file) => {
        return new Promise((resolve) => {
            if (file.size <= MAX_SIZE) {
                resolve(file);
                return;
            }
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => {
                URL.revokeObjectURL(url);
                const canvas = document.createElement('canvas');
                let { width, height } = img;
                const scale = Math.min(1, 1920 / Math.max(width, height));
                width = Math.round(width * scale);
                height = Math.round(height * scale);
                canvas.width = width;
                canvas.height = height;
                canvas.getContext('2d').drawImage(img, 0, 0, width, height);
                canvas.toBlob((blob) => {
                    resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                }, 'image/jpeg', 0.8);
            };
            img.src = url;
        });
    };

    const processFiles = async (files) => {
        const remaining = MAX_PHOTOS - photos.length;
        if (remaining <= 0) return;
        const validFiles = Array.from(files)
            .filter(f => f.type.startsWith('image/'))
            .slice(0, remaining);

        const compressed = await Promise.all(validFiles.map(f => compressImage(f)));

        setPhotos(prev => [...prev, ...compressed]);
        compressed.forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => setPhotoPreviews(prev => [...prev, reader.result]);
            reader.readAsDataURL(file);
        });
    };

    const handlePhotoChange = (e) => {
        processFiles(e.target.files);
        e.target.value = '';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        processFiles(e.dataTransfer.files);
    };

    const removePhoto = (index) => {
        setPhotos(prev => prev.filter((_, i) => i !== index));
        setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (formData.rating === 0) {
            setSubmitStatus('error-rating');
            return;
        }
        setSubmitting(true);
        setSubmitStatus('idle');

        try {
            const photoURLs = [];
            const photoPaths = [];

            for (const file of photos) {
                const fileName = `reviews/${Date.now()}-${Math.random().toString(36).substr(2, 9)}.${file.name.split('.').pop()}`;
                const storageRef = ref(storage, fileName);
                await uploadBytes(storageRef, file);
                photoURLs.push(await getDownloadURL(storageRef));
                photoPaths.push(fileName);
            }

            await addDoc(collection(db, 'reviews'), {
                name: formData.name,
                rating: formData.rating,
                text: formData.text,
                photoURL: photoURLs[0] || null,
                photoPath: photoPaths[0] || null,
                photoURLs,
                photoPaths,
                createdAt: serverTimestamp(),
                hidden: false
            });

            setFormData({ name: '', rating: 0, text: '' });
            setPhotos([]);
            setPhotoPreviews([]);
            setSubmitStatus('success');
            setShowForm(false);
        } catch (error) {
            console.error('Error submitting review:', error);
            setSubmitStatus('error');
        }
        setSubmitting(false);
    };

    return (
        <div className='reviews-section'>
            <h1>WHAT OUR CUSTOMERS SAY</h1>

            {loading ? (
                <p className='reviews__empty'>Loading reviews...</p>
            ) : reviews.length === 0 ? (
                <p className='reviews__empty'>No reviews yet. Be the first to share your experience!</p>
            ) : (
                <div className='reviews__scroll-container'>
                    {reviews.map((review) => (
                        <div key={review.id} className='review-card'>
                            {(review.photoURLs?.length > 0 || review.photoURL) && (
                                <ReviewPhotoSlider
                                    urls={(review.photoURLs || [review.photoURL]).filter(Boolean)}
                                    name={review.name}
                                />
                            )}
                            <StarRating rating={review.rating} size='16px' />
                            <p className='review-card__text'>"{review.text}"</p>
                            <p className='review-card__author'>-- {review.name}</p>
                            <p className='review-card__date'>
                                {review.createdAt?.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                            </p>
                        </div>
                    ))}
                </div>
            )}

            {submitStatus === 'success' && (
                <div className='review-status review-status--success'>
                    Thank you for your review!
                </div>
            )}

            <button
                className='reviews__toggle-btn'
                onClick={() => { setShowForm(!showForm); setSubmitStatus('idle'); }}
            >
                {showForm ? 'CANCEL' : 'WRITE A REVIEW'}
            </button>

            {showForm && (
                <form className='review-form' onSubmit={handleSubmit}>
                    {submitStatus === 'error' && (
                        <div className='review-status review-status--error'>
                            Failed to submit review. Please try again.
                        </div>
                    )}
                    {submitStatus === 'error-rating' && (
                        <div className='review-status review-status--error'>
                            Please select a star rating.
                        </div>
                    )}
                    <div className='review-form__group'>
                        <label htmlFor='review-name'>Name</label>
                        <input
                            type='text'
                            id='review-name'
                            name='name'
                            value={formData.name}
                            onChange={handleChange}
                            placeholder='Your name'
                            required
                            disabled={submitting}
                        />
                    </div>
                    <div className='review-form__stars'>
                        <label>Rating</label>
                        <StarRating
                            rating={formData.rating}
                            onRate={(star) => setFormData({ ...formData, rating: star })}
                            size='28px'
                        />
                    </div>
                    <div className='review-form__group'>
                        <label htmlFor='review-text'>Your Review</label>
                        <textarea
                            id='review-text'
                            name='text'
                            value={formData.text}
                            onChange={handleChange}
                            placeholder='Share your experience...'
                            required
                            disabled={submitting}
                        />
                    </div>
                    <div
                        className={`review-form__photo-upload${dragging ? ' review-form__photo-upload--dragging' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <input
                            type='file'
                            id='review-photo'
                            accept='image/*'
                            multiple
                            onChange={handlePhotoChange}
                            style={{ display: 'none' }}
                        />
                        {photoPreviews.length > 0 && (
                            <div className='review-form__photo-previews'>
                                {photoPreviews.map((preview, index) => (
                                    <div key={index} className='review-form__photo-preview'>
                                        <img src={preview} alt={`Preview ${index + 1}`} />
                                        <button type='button' onClick={() => removePhoto(index)}>
                                            <i className='fas fa-times' />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {photos.length < MAX_PHOTOS && (
                            <label htmlFor='review-photo' className='review-form__photo-label'>
                                <i className='fas fa-camera' />
                                <span>{dragging ? 'Drop photos here' : `Drag photos here or click to upload (${photos.length}/${MAX_PHOTOS})`}</span>
                            </label>
                        )}
                    </div>
                    <button
                        type='submit'
                        className='review-form__submit'
                        disabled={submitting}
                    >
                        {submitting ? 'SUBMITTING...' : 'SUBMIT REVIEW'}
                    </button>
                </form>
            )}
        </div>
    );
}

export default Reviews;
